import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import { IconClipboardList, IconPencil } from "@tabler/icons-react";
import {
  ClientFormStatus,
  ClientFormSummary,
  FormCategory,
} from "../../types/formTemplates";
import { parseServerDateTime, wholeDayDiff } from "../../utils/localDateUtils";
import { clientFormStatusTag } from "../../utils/clientFormStatus";
import styles from "./ClientFormsListSection.module.css";

/** Status synonyms — same logic as the legacy ClientFormsWaiversTab impl. */
const IN_PROGRESS_STATUSES = new Set<ClientFormStatus>([
  "edited",
  "in_progress",
]);
const COMPLETED_STATUSES = new Set<ClientFormStatus>([
  "submitted",
  "completed",
]);
const NOT_STARTED_STATUSES = new Set<ClientFormStatus>([
  "not_started",
  "unopened",
]);
const OPENED_STATUSES = new Set<ClientFormStatus>(["opened", "viewed"]);
const EXPIRED_STATUSES = new Set<ClientFormStatus>(["expired", "past_expiry"]);

function formMatchesStatus(
  form: ClientFormSummary,
  filter: ClientFormStatus | "all",
): boolean {
  if (filter === "all") return true;
  if (filter === "edited") return IN_PROGRESS_STATUSES.has(form.status);
  if (filter === "submitted") return COMPLETED_STATUSES.has(form.status);
  if (filter === "not_started") return NOT_STARTED_STATUSES.has(form.status);
  if (filter === "opened") return OPENED_STATUSES.has(form.status);
  if (filter === "expired") return EXPIRED_STATUSES.has(form.status);
  return form.status === filter;
}

type SortOption =
  | "updated_desc"
  | "updated_asc"
  | "status_asc"
  | "status_desc"
  | "name_asc"
  | "name_desc";

const SORT_SELECT_DATA: { value: SortOption; label: string }[] = [
  { value: "updated_desc", label: "Recently updated" },
  { value: "updated_asc", label: "Oldest updated" },
  { value: "status_asc", label: "Status A→Z" },
  { value: "status_desc", label: "Status Z→A" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
];

const STATUS_FILTER_PILLS: {
  label: string;
  value: "all" | ClientFormStatus;
}[] = [
  { label: "All", value: "all" },
  { label: "Not started", value: "not_started" },
  { label: "Opened", value: "opened" },
  { label: "In Progress", value: "edited" },
  { label: "Completed", value: "submitted" },
  { label: "Expired", value: "expired" },
];

// Canonical status → {label, color} mapping lives in utils/clientFormStatus.ts
// so the same badge colors render in every list (Forms & Waivers, SOAP Notes,
// appointment-detail forms panel). Local alias keeps this file's call sites short.
const statusTag = clientFormStatusTag;

function statusIconBadgeClass(status: unknown): string {
  switch (status) {
    case "not_started":
    case "unopened":
      return styles.iconBadgeStatusNotStarted;
    case "opened":
    case "viewed":
      return styles.iconBadgeStatusOpened;
    case "edited":
    case "in_progress":
      return styles.iconBadgeStatusInProgress;
    case "submitted":
    case "completed":
      return styles.iconBadgeStatusCompleted;
    case "expired":
    case "past_expiry":
      return styles.iconBadgeStatusExpired;
    default:
      return styles.iconBadgeStatusNotStarted;
  }
}

// Mirrors form_utils.constants.TAGGED_CATEGORIES; reusable + per_visit stay untagged.
function categoryBadge(
  category: FormCategory | undefined,
): { label: string; color: string } | null {
  if (category === "per_visit_clinical") {
    return { label: "SOAP", color: "gray" };
  }
  if (category === "post_appointment") {
    return { label: "Post-Appointment", color: "teal" };
  }
  return null;
}

function expirationBadge(
  expiresAt: string | null,
): { label: string; color: string } | null {
  // Per validity-and-messaging.md, per_appointment templates set expires_at
  // at materialization (before submission), so we render the countdown for
  // any form with an expiry — submitted or not. The `isExpiredStatus` check
  // at the call site avoids double-badging forms whose API status is
  // already "expired".
  if (!expiresAt) return null;
  const exp = parseServerDateTime(expiresAt);
  if (!exp) return null;
  const now = new Date();
  if (exp.getTime() <= now.getTime()) return { label: "Expired", color: "red" };
  const diffDays = wholeDayDiff(now, exp);
  if (diffDays === 0) {
    return { label: "Expires today", color: "orange" };
  }
  if (diffDays <= 7) {
    return {
      label: `Expires in ${diffDays} day${diffDays === 1 ? "" : "s"}`,
      color: "orange",
    };
  }
  return {
    label: `Expires ${exp.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })}`,
    color: "gray",
  };
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = parseServerDateTime(iso);
  if (!d) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusSortRank(status: unknown): number {
  if (typeof status !== "string") return 99;
  if (NOT_STARTED_STATUSES.has(status as ClientFormStatus)) return 0;
  if (OPENED_STATUSES.has(status as ClientFormStatus)) return 1;
  if (IN_PROGRESS_STATUSES.has(status as ClientFormStatus)) return 2;
  if (COMPLETED_STATUSES.has(status as ClientFormStatus)) return 3;
  if (EXPIRED_STATUSES.has(status as ClientFormStatus)) return 4;
  return 99;
}

export interface ClientFormsListSectionProps {
  forms: ClientFormSummary[];
  isLoading?: boolean;
  /** Visual density. 'comfortable' = client-details full page; 'compact' = inside a modal/accordion. */
  density?: "comfortable" | "compact";
  emptyTitle?: string;
  emptyDescription?: string;
  onOpenForm: (form: ClientFormSummary) => void;
  /** Per-row 3-dot menu items. Caller decides what actions are valid. */
  renderRowActions?: (form: ClientFormSummary) => React.ReactNode;
  /** Optional extra inline badges on the row title line (e.g. a scope pill). */
  renderRowExtras?: (form: ClientFormSummary) => React.ReactNode;
  /** When provided, a pencil affordance is shown on each row that lets staff
   * set a custom_name override for the instance. Pass null to clear. */
  onRename?: (
    form: ClientFormSummary,
    customName: string | null,
  ) => Promise<void>;
  /** Show the status filter pill row. Defaults to true. Set false where the
   *  caller already provides a sibling filter (e.g. the appointment-detail
   *  panel's category chips). */
  showStatusFilter?: boolean;
}

// Mirrors `_CUSTOM_NAME_MAX_LEN` and the `VARCHAR(255)` column width on
// bookings-infra `client_forms.custom_name`. Source of truth lives on the
// backend (see `form_utils.client_forms.set_client_form_custom_name`); keep
// this constant in sync if that limit ever changes.
const MAX_CUSTOM_NAME_LEN = 255;

export const ClientFormsListSection: React.FC<ClientFormsListSectionProps> = ({
  forms,
  isLoading = false,
  density = "comfortable",
  emptyTitle,
  emptyDescription,
  onOpenForm,
  renderRowActions,
  renderRowExtras,
  onRename,
  showStatusFilter = true,
}) => {
  const [filterRaw, setFilter] = useState<ClientFormStatus | "all">("all");
  // When the status filter is hidden, force "all" so the variable can't be
  // stuck on a stale value from a prior render where it was visible.
  const filter: ClientFormStatus | "all" = showStatusFilter ? filterRaw : "all";
  const [sortBy, setSortBy] = useState<SortOption>("updated_desc");
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  // Synchronous re-entry guard. React batches state, so a flag stored in
  // state can lag behind a second commitRename call fired from the same
  // tick (e.g. Enter handler triggers a blur on the focused input before
  // the editing state has cleared). A ref updates immediately, so the
  // second call sees savingRef.current === true and returns early.
  const savingRef = useRef(false);

  const displayName = useCallback(
    (f: ClientFormSummary) => f.custom_name?.trim() || f.name,
    [],
  );

  const beginRename = useCallback(
    (f: ClientFormSummary) => {
      setEditingFormId(f.id);
      setEditDraft(displayName(f));
    },
    [displayName],
  );

  const cancelRename = useCallback(() => {
    setEditingFormId(null);
    setEditDraft("");
    savingRef.current = false;
  }, []);

  const commitRename = useCallback(
    async (f: ClientFormSummary) => {
      if (!onRename || savingRef.current) return;
      const trimmed = editDraft.trim();
      const original = (f.custom_name ?? "").trim() || f.name;
      // No-op if user didn't change anything.
      if (trimmed === original) {
        cancelRename();
        return;
      }
      // Empty (or whitespace-only) clears the override.
      const next: string | null = trimmed.length === 0 ? null : trimmed;

      // Latch synchronously BEFORE awaiting. The state setters below
      // are batched and the TextInput stays mounted until the next
      // render, so without the ref a same-tick blur (e.g. Enter-fired
      // blur in some browsers) could re-enter and duplicate the POST.
      savingRef.current = true;
      const snapshotDraft = editDraft;
      setEditingFormId(null);
      setEditDraft("");
      try {
        await onRename(f, next);
      } catch {
        // Re-enter edit mode with the user's typed value so they can
        // retry without losing their work. The caller (onRename) is
        // responsible for surfacing the error to the user — typically
        // a red toast — before throwing.
        setEditingFormId(f.id);
        setEditDraft(snapshotDraft);
      } finally {
        savingRef.current = false;
      }
    },
    [onRename, editDraft, cancelRename],
  );

  const filtered = useMemo(
    () => forms.filter((f) => formMatchesStatus(f, filter)),
    [forms, filter],
  );

  const sortedForms = useMemo(() => {
    const list = [...filtered];
    const tieName = (a: ClientFormSummary, b: ClientFormSummary) =>
      displayName(a).localeCompare(displayName(b), undefined, {
        sensitivity: "base",
      });

    switch (sortBy) {
      case "updated_desc":
        list.sort(
          (a, b) =>
            new Date(b.updated_dt).getTime() -
              new Date(a.updated_dt).getTime() || tieName(a, b),
        );
        break;
      case "updated_asc":
        list.sort(
          (a, b) =>
            new Date(a.updated_dt).getTime() -
              new Date(b.updated_dt).getTime() || tieName(a, b),
        );
        break;
      case "status_asc":
        list.sort(
          (a, b) =>
            statusSortRank(a.status) - statusSortRank(b.status) ||
            tieName(a, b),
        );
        break;
      case "status_desc":
        list.sort(
          (a, b) =>
            statusSortRank(b.status) - statusSortRank(a.status) ||
            tieName(a, b),
        );
        break;
      case "name_asc":
        list.sort(tieName);
        break;
      case "name_desc":
        list.sort((a, b) => tieName(b, a));
        break;
    }
    return list;
  }, [filtered, sortBy]);

  const onSelectPill = useCallback((value: "all" | ClientFormStatus) => {
    setFilter(value);
  }, []);

  const iconSize = density === "compact" ? 16 : 20;
  const sortWidth = density === "compact" ? 200 : 260;

  return (
    <Box
      className={`${styles.shell} ${density === "compact" ? styles.compact : ""}`}
    >
      <Group
        justify={showStatusFilter ? "space-between" : "flex-end"}
        align="flex-end"
        wrap="wrap"
        gap={density === "compact" ? "sm" : "md"}
        className={styles.filterSortRow}
      >
        {showStatusFilter && (
          <Group
            gap="xs"
            wrap="wrap"
            role="group"
            aria-label="Filter forms by status. Select All to clear."
            className={styles.pillRow}
          >
            {STATUS_FILTER_PILLS.map((f) => {
              const active =
                f.value === "all" ? filter === "all" : filter === f.value;
              return (
                <UnstyledButton
                  key={f.value}
                  aria-pressed={active}
                  className={`${styles.pill} ${active ? styles.pillToneActive : ""}`}
                  onClick={() => onSelectPill(f.value)}
                >
                  {f.label}
                </UnstyledButton>
              );
            })}
          </Group>
        )}
        <Select
          size={density === "compact" ? "xs" : "sm"}
          w={sortWidth}
          aria-label="Sort forms list"
          data={SORT_SELECT_DATA}
          value={sortBy}
          onChange={(v) => v && setSortBy(v as SortOption)}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      {isLoading && forms.length === 0 ? (
        <Group justify="center" p="lg">
          <Loader size="sm" />
        </Group>
      ) : sortedForms.length === 0 ? (
        <Stack align="center" gap="xs" className={styles.emptyState}>
          <ThemeIcon size={44} radius="xl" variant="light" color="gray">
            <IconClipboardList size={24} />
          </ThemeIcon>
          <Text fw={600}>
            {forms.length === 0
              ? emptyTitle || "No forms assigned yet"
              : "No forms match this filter"}
          </Text>
          <Text c="dimmed" size="sm" ta="center" maw={420}>
            {forms.length === 0
              ? emptyDescription || "Once forms are assigned, they appear here."
              : "Try a different status filter."}
          </Text>
        </Stack>
      ) : (
        <Stack
          gap={density === "compact" ? "xs" : "sm"}
          className={styles.rowList}
        >
          {sortedForms.map((f) => {
            const tag = statusTag(f.status);
            const isExpiredStatus = EXPIRED_STATUSES.has(f.status);
            const expBadge = isExpiredStatus
              ? null
              : expirationBadge(f.expires_at);
            const catBadge = categoryBadge(f.category);
            const isEditing = editingFormId === f.id;
            return (
              <Box key={f.id} className={styles.formRow}>
                <UnstyledButton
                  className={styles.rowBody}
                  onClick={() => !isEditing && onOpenForm(f)}
                  aria-label={`Open form ${displayName(f)}`}
                  component={isEditing ? "div" : undefined}
                >
                  <Box
                    className={`${styles.iconBadge} ${statusIconBadgeClass(f.status)}`}
                  >
                    <IconClipboardList size={iconSize} stroke={1.75} />
                  </Box>
                  <Box className={styles.rowMeta}>
                    <Group gap="xs" wrap="wrap" className={styles.rowTitleLine}>
                      {isEditing ? (
                        <TextInput
                          size={density === "compact" ? "xs" : "sm"}
                          value={editDraft}
                          autoFocus
                          maxLength={MAX_CUSTOM_NAME_LEN}
                          placeholder={f.name}
                          aria-label={`Rename form ${f.name}`}
                          onChange={(e) => setEditDraft(e.currentTarget.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              void commitRename(f);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          onBlur={() => void commitRename(f)}
                        />
                      ) : (
                        <Text
                          fw={600}
                          size={density === "compact" ? "xs" : "sm"}
                        >
                          {displayName(f)}
                        </Text>
                      )}
                      <Badge
                        size="xs"
                        variant="light"
                        color="gray"
                        style={{ textTransform: "none" }}
                      >
                        v{f.source_version}
                      </Badge>
                      {catBadge && (
                        <Badge
                          variant="light"
                          color={catBadge.color}
                          size={density === "compact" ? "xs" : "sm"}
                          aria-label={`Form type: ${catBadge.label}`}
                          style={{ textTransform: "none" }}
                        >
                          {catBadge.label}
                        </Badge>
                      )}
                      <Badge
                        variant="light"
                        color={tag.color}
                        size={density === "compact" ? "xs" : "sm"}
                        aria-label={`Form status: ${tag.label}`}
                      >
                        {tag.label}
                      </Badge>
                      {expBadge && (
                        <Badge
                          variant="light"
                          color={expBadge.color}
                          size={density === "compact" ? "xs" : "sm"}
                          aria-label={expBadge.label}
                          style={{ textTransform: "none" }}
                        >
                          {expBadge.label}
                        </Badge>
                      )}
                      {renderRowExtras?.(f)}
                    </Group>
                    <Text className={styles.rowSubLine}>
                      Updated {formatDate(f.updated_dt)}
                    </Text>
                  </Box>
                </UnstyledButton>
                {/* Rename pencil lives OUTSIDE the rowBody UnstyledButton so
                 * we don't render a <button> nested inside another <button>
                 * (invalid HTML / React validateDOMNesting warning). */}
                {!isEditing && onRename && (
                  <Box className={styles.rowActions}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size={density === "compact" ? "sm" : "md"}
                      aria-label={`Rename form ${displayName(f)}`}
                      onClick={() => beginRename(f)}
                    >
                      <IconPencil size={14} stroke={1.75} />
                    </ActionIcon>
                  </Box>
                )}
                {renderRowActions && (
                  <Box className={styles.rowActions}>{renderRowActions(f)}</Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
